import React, { useEffect, useState } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../App';

const BASE = '/api';
const getToken = () => localStorage.getItem('token');

const PAYMENT_METHODS = ['Наличные', 'Расчётный счёт', 'На карту'];
const fmt = n => new Intl.NumberFormat('ru-RU').format(n || 0) + ' ₽';
const fmtDate = d => { if (!d) return null; const [y, m, day] = d.split('-'); return `${day}-${m}-${y.slice(2)}`; };

const fmtPhone = p => {
  if (!p) return '—';
  const d = p.replace(/\D/g, '');
  if (d.length === 11) return `+7(${d.slice(1,4)})${d.slice(4,7)}-${d.slice(7,9)}-${d.slice(9,11)}`;
  if (d.length === 10) return `+7(${d.slice(0,3)})${d.slice(3,6)}-${d.slice(6,8)}-${d.slice(8,10)}`;
  return p;
};

const METHOD_CODE = {
  'Наличные':           { code: 'Н', color: '#f0a500' },
  'Расчётный счёт': { code: 'Б', color: '#4f8ef7' },
  'На карту':           { code: 'К', color: '#9b6ef7' },
};

const STATUS_LABEL = { active: 'Активный', closed: 'Закрыт', terminated: 'Расторгнут' };
const STATUS_COLOR = { active: 'var(--green)', closed: 'var(--text3)', terminated: 'var(--red)' };

// ─── helpers ─────────────────────────────────────────────
const calcFirstMonth = (startDate, rate) => {
  if (!startDate || !rate || Number(rate) <= 0) return null;
  const d = new Date(startDate);
  const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const daysRemaining = daysInMonth - d.getDate() + 1;
  return { days: daysRemaining, total: daysInMonth, amount: Math.round((daysRemaining / daysInMonth) * Number(rate)) };
};

const fmtDateLong = d => {
  if (!d) return '';
  const months = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
  const dt = new Date(d);
  return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()} г.`;
};

// ─── Модал создания/редактирования договора ───────────────
function ContractModal({ contract, studios, onClose, onSave }) {
  const empty = {
    studio_id: '', last_name: '', first_name: '', middle_name: '', birth_date: '',
    passport_series: '', passport_number: '', passport_issued_by: '', passport_issued_date: '',
    phone: '', email: '', monthly_rate: '', deposit_amount: '', start_date: '',
    end_date: '', payment_method: 'Наличные', status: 'active', notes: ''
  };
  const [form, setForm] = useState(contract ? { ...contract } : empty);
  const [err, setErr] = useState('');
  const [step, setStep] = useState('form'); // 'form' | 'preview'
  // deposit split
  const [depositSplit, setDepositSplit] = useState('once'); // 'once' | '2' | '3'
  const [depositParts, setDepositParts] = useState(['', '', '']);
  // generate state
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null); // { url, sumFirstMonth, depositSchedule }
  const [genErr, setGenErr] = useState('');
  // notify after generate
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const s = f => setForm(p => ({ ...p, ...f }));

  useEffect(() => {
    get('/templates').then(ts => { setTemplates(ts); if (ts.length) setTemplateId(String(ts[0].id)); }).catch(() => {});
  }, []);

  // sync deposit parts[0] when deposit_amount or split changes
  useEffect(() => {
    const total = Number(form.deposit_amount) || 0;
    if (depositSplit === 'once') {
      setDepositParts([String(total), '', '']);
    } else if (depositSplit === '2') {
      setDepositParts(p => [p[0] || String(Math.ceil(total / 2)), p[1] || String(Math.floor(total / 2)), '']);
    } else if (depositSplit === '3') {
      const third = Math.floor(total / 3);
      setDepositParts(p => [p[0] || String(third), p[1] || String(third), p[2] || String(total - third * 2)]);
    }
  }, [depositSplit]); // eslint-disable-line

  const firstMonth = calcFirstMonth(form.start_date, form.monthly_rate);

  const canGenerate = form.last_name && form.first_name && form.passport_series && form.passport_number && form.email;

  const saveContract = async () => {
    if (!form.studio_id || !form.last_name || !form.first_name || !form.monthly_rate || !form.start_date)
      throw new Error('Заполните обязательные поля: студия, ФИО, ставка, дата начала');
    if (contract?.id) { await put(`/contracts/${contract.id}`, form); return contract.id; }
    else { const r = await post('/contracts', form); return r.id; }
  };

  const save = async () => {
    setErr('');
    try { await saveContract(); onSave(); }
    catch (e) { setErr(e.message); }
  };

  const generate = async () => {
    setErr(''); setGenErr('');
    if (!templateId) return setErr('Выберите шаблон договора');
    setGenerating(true);
    try {
      const id = await saveContract();
      const parts = depositSplit === 'once'
        ? [Number(depositParts[0]) || Number(form.deposit_amount) || 0]
        : depositSplit === '2'
          ? [Number(depositParts[0]) || 0, Number(depositParts[1]) || 0]
          : [Number(depositParts[0]) || 0, Number(depositParts[1]) || 0, Number(depositParts[2]) || 0];
      const result = await post(`/contracts/${id}/generate`, { template_id: Number(templateId), deposit_parts: parts });
      setGenResult({ ...result, contractId: id });
      setStep('preview');
    } catch (e) { setErr(e.message); }
    finally { setGenerating(false); }
  };

  const sendSign = async () => {
    setSendingEmail(true);
    try {
      await post('/notifications/send', { contract_id: genResult.contractId, type: 'contract' });
      setEmailSent(true);
    } catch (e) { setGenErr(e.message); }
    finally { setSendingEmail(false); }
  };

  // ── Preview step ────────────────────────────────────────
  if (step === 'preview') {
    return (
      <div className="modal-overlay">
        <div className="modal" style={{ maxWidth: 540 }}>
          <div className="modal-title">Договор сгенерирован</div>
          <div style={{ padding: '8px 0 20px' }}>
            <div style={{ background: 'rgba(34,197,94,.1)', border: '1px solid rgba(34,197,94,.3)', borderRadius: 8, padding: '14px 16px', marginBottom: 20, fontSize: 14 }}>
              <div style={{ fontWeight: 700, color: 'var(--green)', marginBottom: 8 }}>✅ Файл готов</div>
              <a
                href={`${BASE}${genResult.url}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: 'var(--accent)', fontWeight: 600 }}
              >
                Скачать .docx
              </a>
            </div>

            {genResult.sumFirstMonth != null && (
              <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text2)' }}>
                <strong>Сумма к оплате при заключении:</strong>{' '}
                <span style={{ color: 'var(--green)', fontWeight: 700 }}>{fmt(genResult.sumFirstMonth)}</span>
                {firstMonth && <span style={{ color: 'var(--text3)' }}> ({firstMonth.days} дн. из {firstMonth.total})</span>}
              </div>
            )}

            {genResult.depositSchedule?.length > 1 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>График залога:</div>
                {genResult.depositSchedule.map((p, i) => (
                  <div key={i} style={{ fontSize: 13, color: 'var(--text2)', padding: '4px 0' }}>
                    Часть {i + 1}: <strong>{fmt(p)}</strong>
                  </div>
                ))}
              </div>
            )}

            {genErr && <div className="error-msg" style={{ marginBottom: 12 }}>{genErr}</div>}
            {emailSent && <div style={{ color: 'var(--green)', fontSize: 13, marginBottom: 12 }}>✅ Письмо отправлено на {form.email}</div>}
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={() => { setStep('form'); setGenResult(null); setEmailSent(false); }}>
              Вернуться к редактированию
            </button>
            <button className="btn btn-primary" onClick={sendSign} disabled={sendingEmail || emailSent || !form.email}>
              {sendingEmail ? 'Отправка...' : emailSent ? 'Отправлено' : 'Отправить на подпись'}
            </button>
            <button className="btn btn-ghost" onClick={() => { onSave(); }}>Закрыть</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form step ───────────────────────────────────────────
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 720 }}>
        <div className="modal-title">{contract?.id ? 'Договор — редактирование' : 'Новый договор'}</div>
        {err && <div className="error-msg" style={{ marginBottom: 16 }}>{err}</div>}

        <div className="section-title">Объект аренды</div>
        <div className="form-grid" style={{ marginBottom: 20 }}>
          <div className="form-group span-2">
            <label>Студия *</label>
            <select value={form.studio_id} onChange={e => s({ studio_id: e.target.value })}>
              <option value="">Выберите студию...</option>
              {studios.map(st => (
                <option key={st.id} value={st.id}>
                  {st.number}{st.property_number ? ` (${st.property_number})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="section-title">Данные арендатора</div>
        <div className="form-grid" style={{ marginBottom: 20 }}>
          <div className="form-group"><label>Фамилия *</label><input value={form.last_name} onChange={e => s({ last_name: e.target.value })} /></div>
          <div className="form-group"><label>Имя *</label><input value={form.first_name} onChange={e => s({ first_name: e.target.value })} /></div>
          <div className="form-group"><label>Отчество</label><input value={form.middle_name || ''} onChange={e => s({ middle_name: e.target.value })} /></div>
          <div className="form-group"><label>Дата рождения</label><input type="date" value={form.birth_date || ''} onChange={e => s({ birth_date: e.target.value })} /></div>
          <div className="form-group"><label>Серия паспорта</label><input value={form.passport_series || ''} onChange={e => s({ passport_series: e.target.value })} placeholder="1234" /></div>
          <div className="form-group"><label>Номер паспорта</label><input value={form.passport_number || ''} onChange={e => s({ passport_number: e.target.value })} placeholder="567890" /></div>
          <div className="form-group span-2"><label>Кем выдан</label><input value={form.passport_issued_by || ''} onChange={e => s({ passport_issued_by: e.target.value })} /></div>
          <div className="form-group"><label>Дата выдачи паспорта</label><input type="date" value={form.passport_issued_date || ''} onChange={e => s({ passport_issued_date: e.target.value })} /></div>
          <div className="form-group"><label>Телефон</label><input value={form.phone || ''} onChange={e => s({ phone: e.target.value })} onBlur={e => { const d = e.target.value.replace(/\D/g,''); if (!d) return; const t = (d.startsWith('7')||d.startsWith('8') ? d.slice(1) : d).slice(0,10); if (t.length===10) s({ phone: `+7(${t.slice(0,3)})${t.slice(3,6)}-${t.slice(6,8)}-${t.slice(8,10)}` }); }} placeholder="+7(900)000-00-00" /></div>
          <div className="form-group span-2"><label>Email</label><input type="email" value={form.email || ''} onChange={e => s({ email: e.target.value })} placeholder="tenant@example.com" /></div>
        </div>

        <div className="section-title">Условия договора</div>
        <div className="form-grid">
          <div className="form-group"><label>Ежемесячная ставка (₽) *</label><input type="number" value={form.monthly_rate} onChange={e => s({ monthly_rate: e.target.value })} placeholder="30000" /></div>
          <div className="form-group">
            <label>Способ оплаты *</label>
            <select value={form.payment_method} onChange={e => s({ payment_method: e.target.value })}>
              {PAYMENT_METHODS.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Дата начала *</label><input type="date" value={form.start_date} onChange={e => s({ start_date: e.target.value })} /></div>
          <div className="form-group"><label>Дата окончания</label><input type="date" value={form.end_date || ''} onChange={e => s({ end_date: e.target.value })} /></div>
          {contract?.id && (
            <div className="form-group">
              <label>Статус</label>
              <select value={form.status} onChange={e => s({ status: e.target.value })}>
                <option value="active">Активный</option>
                <option value="closed">Закрыт</option>
                <option value="terminated">Расторгнут</option>
              </select>
            </div>
          )}
        </div>

        {/* First month calculation */}
        {firstMonth && (
          <div style={{ background: 'rgba(79,142,247,.08)', border: '1px solid rgba(79,142,247,.2)', borderRadius: 8, padding: '12px 16px', marginTop: 16, fontSize: 13 }}>
            <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--accent)' }}>Расчёт оплаты при заключении</div>
            <div style={{ color: 'var(--text2)' }}>
              С {fmtDateLong(form.start_date)} до конца месяца: {firstMonth.days} дн. из {firstMonth.total} →{' '}
              <strong style={{ color: 'var(--green)' }}>{fmt(firstMonth.amount)}</strong>
            </div>
            <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 4 }}>
              Далее с 1-го числа — полная ставка {fmt(Number(form.monthly_rate))} / мес
            </div>
          </div>
        )}

        {/* Deposit section */}
        <div style={{ marginTop: 20 }}>
          <div className="section-title">Залог</div>
          <div className="form-grid" style={{ marginBottom: 12 }}>
            <div className="form-group">
              <label>Сумма залога (₽)</label>
              <input type="number" value={form.deposit_amount || ''} onChange={e => s({ deposit_amount: e.target.value })} placeholder="30000" />
            </div>
            <div className="form-group">
              <label>Порядок внесения</label>
              <select value={depositSplit} onChange={e => setDepositSplit(e.target.value)}>
                <option value="once">Единовременно</option>
                <option value="2">2 части</option>
                <option value="3">3 части</option>
              </select>
            </div>
          </div>
          {depositSplit !== 'once' && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              {Array.from({ length: depositSplit === '2' ? 2 : 3 }, (_, i) => (
                <div className="form-group" key={i} style={{ margin: 0, flex: '1 1 120px' }}>
                  <label style={{ fontSize: 12 }}>Часть {i + 1} (₽)</label>
                  <input
                    type="number"
                    value={depositParts[i]}
                    onChange={e => setDepositParts(p => { const n = [...p]; n[i] = e.target.value; return n; })}
                    placeholder="0"
                  />
                </div>
              ))}
              <div style={{ flex: '1 1 120px', display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
                <span style={{ fontSize: 12, color: 'var(--text3)' }}>
                  Итого: {fmt(depositParts.slice(0, depositSplit === '2' ? 2 : 3).reduce((s, v) => s + (Number(v) || 0), 0))}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        <div style={{ marginTop: 16 }}>
          <div className="form-group">
            <label>Примечания</label>
            <textarea value={form.notes || ''} onChange={e => s({ notes: e.target.value })} />
          </div>
        </div>

        {/* Generate — template selector */}
        <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--bg3)', borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Шаблон договора</div>
          {templates.length > 0 ? (
            <>
              <select value={templateId} onChange={e => setTemplateId(e.target.value)} style={{ width: '100%' }}>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {!canGenerate && (
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
                  Для генерации заполните: ФИО, серию и номер паспорта, email
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>
              Шаблоны не загружены — добавьте .docx в <strong>Настройки</strong>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-secondary" onClick={save}>Сохранить</button>
          <button
            className="btn btn-primary"
            onClick={generate}
            disabled={!canGenerate || generating || templates.length === 0}
            title={templates.length === 0 ? 'Загрузите шаблон в Настройках' : canGenerate ? '' : 'Заполните ФИО, паспортные данные и email'}
          >
            {generating ? 'Генерация...' : 'Сгенерировать договор'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Модал email-уведомления ──────────────────────────────
function NotifyModal({ contract, onClose }) {
  const [type, setType] = useState('payment');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const send = async () => {
    setLoading(true); setErr('');
    try {
      await post('/notifications/send', { contract_id: contract.id, type });
      setDone(true);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-title">Email-уведомление</div>
        {done ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <p style={{ color: 'var(--green)' }}>Письмо отправлено на {contract.email}</p>
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={onClose}>Закрыть</button>
          </div>
        ) : (
          <>
            {err && <div className="error-msg" style={{ marginBottom: 16 }}>{err}</div>}
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
              Получатель: <strong>{contract.last_name} {contract.first_name}</strong> ({contract.email || 'email не указан'})
            </p>
            <div className="form-grid cols-1" style={{ marginBottom: 20 }}>
              {[
                { value: 'payment',  label: '💰 Напоминание об оплате аренды' },
                { value: 'deposit',  label: '🔒 Напоминание о доплате залога' },
                { value: 'contract', label: '📄 Просьба подписать договор' },
              ].map(opt => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '10px 12px', borderRadius: 8, border: `1px solid ${type === opt.value ? 'var(--accent)' : 'var(--border)'}`, background: type === opt.value ? 'rgba(79,142,247,.1)' : 'transparent' }}>
                  <input type="radio" value={opt.value} checked={type === opt.value} onChange={() => setType(opt.value)} />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
              <button className="btn btn-primary" onClick={send} disabled={loading || !contract.email}>
                {loading ? 'Отправка...' : 'Отправить'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Модал закрытия договора (возврат/удержание залога) ───
function CloseContractModal({ contract, onClose, onSave }) {
  const depPaid = contract.deposit_paid || 0;
  const [returnAmount, setReturnAmount] = useState(String(depPaid || ''));
  const [penaltyAmount, setPenaltyAmount] = useState('0');
  const [paymentMethod, setPaymentMethod] = useState(contract.payment_method || 'Наличные');
  // Дата закрытия: последний день по договору, если он уже прошёл (короткое проживание
  // закрывают задним числом), иначе сегодня
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(contract.end_date && contract.end_date < today ? contract.end_date : today);
  const [description, setDescription] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const ret = Number(returnAmount) || 0;
  const pen = Number(penaltyAmount) || 0;
  const sum = ret + pen;
  const over = sum > depPaid + 0.01;
  const keep = depPaid - sum; // остаток залога, который при закрытии не возвращается и не удерживается

  const preset = (r, p) => { setReturnAmount(String(r)); setPenaltyAmount(String(p)); };

  const submit = async () => {
    setErr('');
    if (ret < 0 || pen < 0) return setErr('Суммы не могут быть отрицательными');
    if (over) return setErr(`Сумма возврата и удержания (${fmt(sum)}) превышает внесённый залог (${fmt(depPaid)})`);
    if (!date) return setErr('Укажите дату закрытия');
    if (!window.confirm(`Закрыть договор датой ${date.split('-').reverse().join('.')}?` +
      (sum > 0 ? ' Будут созданы операции по залогу.' : '') +
      (keep > 0.01 ? ` Залог ${fmt(keep)} не возвращается сейчас и остаётся за арендатором.` : ''))) return;
    setSaving(true);
    try {
      await post(`/contracts/${contract.id}/close`, {
        return_amount: ret, penalty_amount: pen,
        payment_method: paymentMethod, date, description: description || null,
      });
      onSave();
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-title">Закрыть договор</div>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
          №{contract.studio_number} — <strong>{contract.last_name} {contract.first_name}</strong>
          {depPaid > 0 && <> · залог внесён: <strong>{fmt(depPaid)}</strong></>}
        </p>
        {err && <div className="error-msg" style={{ marginBottom: 16 }}>{err}</div>}

        {depPaid > 0 ? (
          <>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => preset(depPaid, 0)}>Вернуть всё</button>
              <button className="btn btn-ghost btn-sm" onClick={() => preset(0, depPaid)}>Удержать всё (штраф)</button>
              <button className="btn btn-ghost btn-sm" onClick={() => preset(Math.round(depPaid / 2), depPaid - Math.round(depPaid / 2))}>Пополам</button>
              <button className="btn btn-ghost btn-sm" onClick={() => preset(0, 0)}>Без возврата залога</button>
            </div>
            <div className="form-grid" style={{ marginBottom: 12 }}>
              <div className="form-group">
                <label>Вернуть арендатору (₽)</label>
                <input type="number" value={returnAmount} onChange={e => setReturnAmount(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Удержать, штраф (₽)</label>
                <input type="number" value={penaltyAmount} onChange={e => setPenaltyAmount(e.target.value)} />
              </div>
            </div>
            <div style={{ fontSize: 12, color: over ? 'var(--red)' : 'var(--text3)', marginBottom: 12 }}>
              Итого: {fmt(sum)} из {fmt(depPaid)}{over && ' — превышение внесённого залога!'}
              {!over && keep > 0.01 && <> · не возвращается сейчас: <strong>{fmt(keep)}</strong> — остаётся за арендатором,
                вернуть позже можно операцией «Возврат залога» по этому договору</>}
            </div>
            {sum > 0 && <div className="form-grid" style={{ marginBottom: 12 }}>
              <div className="form-group">
                <label>Способ оплаты</label>
                <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                  {PAYMENT_METHODS.map(m => <option key={m}>{m}</option>)}
                </select>
                {paymentMethod === 'На карту' && pen > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--yellow)', marginTop: 4 }}>⚠ Удержанная сумма автоматически будет выведена дивидендами АК</div>
                )}
              </div>
            </div>}
            <div className="form-group" style={{ marginBottom: 4 }}>
              <label>Примечание (причина удержания)</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Например: порча имущества, досрочный съезд..." />
            </div>
          </>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12 }}>Залог по договору не вносился — договор будет просто расторгнут.</p>
        )}

        <div className="form-group" style={{ marginTop: 12, marginBottom: 4 }}>
          <label>Дата закрытия (последний день проживания)</label>
          <input type="date" value={date} max={today} onChange={e => setDate(e.target.value)} />
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-danger" onClick={submit} disabled={saving}>
            {saving ? 'Закрытие...' : 'Закрыть договор'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Главная страница ─────────────────────────────────────
export default function ContractsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [contracts, setContracts] = useState([]);
  const [studios, setStudios] = useState([]);
  const [archive, setArchive] = useState(false);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);       // contract obj или {} для нового
  const [notifyModal, setNotifyModal] = useState(null);
  const [closeModal, setCloseModal] = useState(null);

  const load = () => {
    get('/contracts?all=1').then(setContracts).catch(() => {});
    get('/studios').then(setStudios).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const filtered = contracts.filter(c => {
    const isActive = c.status === 'active';
    if (archive && isActive) return false;
    if (!archive && !isActive) return false;
    const q = search.toLowerCase();
    return !q || `${c.last_name} ${c.first_name} ${c.middle_name || ''} ${c.studio_number} ${c.phone || ''}`.toLowerCase().includes(q);
  });

  const thStyle = { padding: '6px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' };
  const thRight = { ...thStyle, textAlign: 'right' };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          Договоры с арендаторами
          <span>{filtered.length} записей</span>
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`btn ${!archive ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setArchive(false)}>Активные</button>
          <button className={`btn ${archive ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setArchive(true)}>Архив</button>
          <button className="btn btn-primary" onClick={() => setModal({})}>+ Новый договор</button>
        </div>
      </div>

      {/* Легенда */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text3)' }}>
        {Object.entries(METHOD_CODE).map(([label, { code, color }]) => (
          <div key={code} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ background: color, color: '#fff', borderRadius: 3, padding: '1px 5px', fontWeight: 700, fontSize: 10 }}>{code}</span>
            {label}
          </div>
        ))}
      </div>

      <div className="filters">
        <input placeholder="Поиск по ФИО, студии, телефону..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 300 }} />
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--bg3)', borderBottom: '1px solid var(--border)' }}>
                <th style={thStyle}>СТУДИЯ</th>
                <th style={thStyle}>КВАРТИРА</th>
                <th style={thStyle}>ФИО</th>
                <th style={thStyle}>ТЕЛЕФОН</th>
                <th style={thStyle}>ЗАЕЗД</th>
                <th style={thStyle}>ОКОНЧАНИЕ</th>
                <th style={thRight}>АРЕНДА/МЕС</th>
                <th style={thStyle}>ОПЛАТА</th>
                <th style={thRight}>ЗАЛОГ (ВНЕС./ВСЕГО)</th>
                {archive && <th style={thStyle}>СТАТУС</th>}
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={11} style={{ textAlign: 'center', color: 'var(--text3)', padding: 40 }}>
                  {archive ? 'Архив пуст' : 'Нет активных договоров'}
                </td></tr>
              )}
              {filtered.map((c, idx) => {
                const depPaid = c.deposit_paid || 0;
                const depTotal = c.deposit_amount || 0;
                const depRemaining = Math.max(0, depTotal - depPaid);
                const depPct = depTotal > 0 ? Math.round(depPaid / depTotal * 100) : 100;
                const mc = METHOD_CODE[c.payment_method];
                return (
                  <tr
                    key={c.id}
                    style={{
                      borderBottom: '1px solid var(--border)',
                      background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                      opacity: archive ? 0.75 : 1,
                      cursor: 'pointer',
                    }}
                    onClick={() => setModal(c)}
                  >
                    <td style={{ padding: '5px 10px', fontWeight: 700, whiteSpace: 'nowrap' }}>{c.studio_number}</td>
                    <td style={{ padding: '5px 10px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{c.property_number || '—'}</td>
                    <td style={{ padding: '5px 10px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600 }}>{c.last_name} {c.first_name} {c.middle_name || ''}</div>
                      {c.email && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{c.email}</div>}
                    </td>
                    <td style={{ padding: '5px 10px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{fmtPhone(c.phone)}</td>
                    <td style={{ padding: '5px 10px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{fmtDate(c.start_date) || '—'}</td>
                    <td style={{ padding: '5px 10px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                      {c.end_date ? fmtDate(c.end_date) : <span style={{ color: 'var(--text3)' }}>∞</span>}
                    </td>
                    <td style={{ padding: '5px 10px', fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(c.monthly_rate)}</td>
                    <td style={{ padding: '5px 10px', whiteSpace: 'nowrap' }}>
                      {mc
                        ? <span style={{ background: mc.color, color: '#fff', borderRadius: 3, padding: '1px 6px', fontWeight: 700, fontSize: 11 }}>{mc.code}</span>
                        : <span style={{ color: 'var(--text3)', fontSize: 11 }}>{c.payment_method}</span>
                      }
                    </td>
                    <td style={{ padding: '5px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {depTotal > 0 ? (
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: depRemaining > 0 ? 'var(--yellow)' : 'var(--green)' }}>
                            {fmt(depPaid)} / {fmt(depTotal)}
                          </div>
                          {depRemaining > 0 && <div style={{ fontSize: 11, color: 'var(--text3)' }}>осталось {fmt(depRemaining)}</div>}
                          <div style={{ background: 'var(--bg3)', borderRadius: 4, height: 4, marginTop: 4, overflow: 'hidden' }}>
                            <div style={{ background: depRemaining > 0 ? 'var(--yellow)' : 'var(--green)', width: `${depPct}%`, height: '100%' }} />
                          </div>
                        </div>
                      ) : <span style={{ color: 'var(--text3)' }}>—</span>}
                    </td>
                    {archive && (
                      <td style={{ padding: '5px 10px', whiteSpace: 'nowrap', fontSize: 11, color: STATUS_COLOR[c.status] || 'var(--text3)', fontWeight: 600 }}>
                        {STATUS_LABEL[c.status] || c.status}
                      </td>
                    )}
                    <td style={{ padding: '5px 10px' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {!archive && c.email && (
                          <button className="btn btn-ghost btn-sm" onClick={() => setNotifyModal(c)} title="Отправить email">✉️</button>
                        )}
                        {isAdmin && c.status === 'active' && (
                          <button className="btn btn-danger btn-sm" onClick={() => setCloseModal(c)} title="Закрыть договор">⛔</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modal !== null && (
        <ContractModal
          contract={modal.id ? modal : null}
          studios={studios}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}
      {notifyModal && <NotifyModal contract={notifyModal} onClose={() => setNotifyModal(null)} />}
      {closeModal && (
        <CloseContractModal
          contract={closeModal}
          onClose={() => setCloseModal(null)}
          onSave={() => { setCloseModal(null); load(); }}
        />
      )}
    </div>
  );
}
